import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Alert, Badge } from 'react-bootstrap';
import API from './api';

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('user');
  const [resetPassword, setResetPassword] = useState('');
  const [formError, setFormError] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await API.get('/api/users');
      if (response.error) {
        setError(response.error);
      } else {
        setUsers(response.users || []);
      }
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!newUsername || !newPassword) {
      setFormError('Username and password are required');
      return;
    }

    if (newPassword.length < 4) {
      setFormError('Password must be at least 4 characters');
      return;
    }

    setFormLoading(true);

    try {
      const response = await API.post('/api/users', {
        username: newUsername,
        password: newPassword,
        role: newRole
      });

      if (response.error) {
        setFormError(response.error);
      } else {
        setShowCreateModal(false);
        setNewUsername('');
        setNewPassword('');
        setNewRole('user');
        loadUsers();
      }
    } catch (err) {
      setFormError('Failed to create user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) {
      return;
    }

    try {
      const response = await API.delete(`/api/users/${userId}`);
      if (response.error) {
        setError(response.error);
      } else {
        loadUsers();
      }
    } catch (err) {
      setError('Failed to delete user');
    }
  };

  const handleToggleRole = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';

    try {
      const response = await API.patch(`/api/users/${userId}`, {
        role: newRole
      });

      if (response.error) {
        setError(response.error);
      } else {
        loadUsers();
      }
    } catch (err) {
      setError('Failed to update user role');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!resetPassword) {
      setFormError('Password is required');
      return;
    }

    if (resetPassword.length < 4) {
      setFormError('Password must be at least 4 characters');
      return;
    }

    setFormLoading(true);

    try {
      const response = await API.post(`/api/users/${selectedUser.id}/password`, {
        new_password: resetPassword
      });

      if (response.error) {
        setFormError(response.error);
      } else {
        setShowResetModal(false);
        setResetPassword('');
        setSelectedUser(null);
      }
    } catch (err) {
      setFormError('Failed to reset password');
    } finally {
      setFormLoading(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>User Management</h2>
        <Button
          variant="primary"
          onClick={() => setShowCreateModal(true)}
        >
          Create User
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <Card>
        <Card.Body>
          <Table hover responsive>
            <thead>
              <tr>
                <th>Username</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>{user.username}</td>
                  <td>
                    <Badge bg={user.role === 'admin' ? 'danger' : 'secondary'}>
                      {user.role}
                    </Badge>
                  </td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleToggleRole(user.id, user.role)}
                    >
                      Make {user.role === 'admin' ? 'User' : 'Admin'}
                    </Button>
                    <Button
                      variant="outline-warning"
                      size="sm"
                      className="me-2"
                      onClick={() => {
                        setSelectedUser(user);
                        setShowResetModal(true);
                      }}
                    >
                      Reset Password
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteUser(user.id, user.username)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Create User Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger">{formError}</Alert>}
          <Form onSubmit={handleCreateUser}>
            <Form.Group className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                disabled={formLoading}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={formLoading}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                disabled={formLoading}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </Form.Select>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setShowCreateModal(false)}
                disabled={formLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={formLoading}
              >
                {formLoading ? 'Creating...' : 'Create User'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Reset Password Modal */}
      <Modal show={showResetModal} onHide={() => setShowResetModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reset Password for {selectedUser?.username}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger">{formError}</Alert>}
          <Form onSubmit={handleResetPassword}>
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                disabled={formLoading}
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setShowResetModal(false)}
                disabled={formLoading}
              >
                Cancel
              </Button>
              <Button
                variant="warning"
                type="submit"
                disabled={formLoading}
              >
                {formLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default Users;
